import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { onboardingApi } from '../lib/api';

/**
 * Guided tour using react-joyride v3.
 * - Manual-only: launches when forceStart=true (from Company Settings → Tour)
 * - Auto-launch is disabled until step targeting is improved
 */
export default function OnboardingTour({ forceStart = false, onClose }) {
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (forceStart) setRun(true);
  }, [forceStart]);

  // On mobile, the sidebar is collapsed by default. Most tour steps point at
  // `nav-*` items inside that sidebar, so we ask the Layout to keep it open while
  // the tour is running. Layout listens for these custom events.
  useEffect(() => {
    if (!run) return;
    window.dispatchEvent(new CustomEvent('tour:open-sidebar'));
    return () => window.dispatchEvent(new CustomEvent('tour:close-sidebar'));
  }, [run]);

  // v3 step shape — `placement: 'center'` works without a target via `target: 'body'`.
  // `buttons` controls which footer buttons render (v3 changed from v2's showSkipButton).
  const stepButtons = ['back', 'close', 'skip', 'primary'];
  const steps = [
    {
      target: 'body',
      placement: 'center',
      title: '👋 Welcome to My Dispatch',
      content: 'Your all-in-one trucking operations platform. This guided tour walks you through every major feature in under 3 minutes. Skip any time — re-run from Company Settings → Tour.',
      disableBeacon: true,
      buttons: stepButtons,
    },
    { target: '[data-testid="nav-dashboard"]', placement: 'right',
      title: '📊 Dashboard',
      content: 'Your command center: weekly revenue at a glance, active loads, driver activity charts, and pending deductions. Start every morning here.',
      buttons: stepButtons },
    { target: '[data-testid="nav-summary"]', placement: 'right',
      title: '💵 Weekly Summary',
      content: 'Per-unit financial breakdown: gross, fees, deductions, net payout. One-click **Email Settlement** to the owner-operator, **Email All Settlements** in bulk, mark Paid, share via SMS, or download a branded PDF.',
      buttons: stepButtons },
    { target: '[data-testid="nav-loads"]', placement: 'right',
      title: '🚛 Loads',
      content: 'Every load by driver, broker, and week. One-click Amazon Relay CSV/XLSX import. Per-load assignment overrides for driver, owner-op, and dispatcher right on the form.',
      buttons: stepButtons },
    { target: '[data-testid="nav-drivers"]', placement: 'right',
      title: '👤 Drivers',
      content: 'Manage driver profiles, contact info, pay type (8 methods supported), and assigned unit. Click **Paystub** to preview a paystub for any week with full PDF download.',
      buttons: stepButtons },
    { target: '[data-testid="nav-deductions"]', placement: 'right',
      title: '➖ Deductions',
      content: 'Per-unit / per-week deductions for fuel, parts, advances, insurance, escrow, and more. Recurring rules supported, with YTD totals tracked automatically.',
      buttons: stepButtons },
    { target: '[data-testid="nav-units"]', placement: 'right',
      title: '🛻 Units',
      content: 'Your full fleet — trucks, trailers, VINs, plates, IFTA, inspections, document expiry tracking. Assign drivers, owner-ops, and dispatchers from one place.',
      buttons: stepButtons },
    { target: '[data-testid="nav-group-people"]', placement: 'right',
      title: '👥 People',
      content: 'Staff, Owner-Operators, Dispatchers, and the unified Paystubs Hub — all the human side of your operation in one collapsible group.',
      buttons: stepButtons },
    { target: '[data-testid="nav-paystubs"]', placement: 'right',
      title: '🧾 Paystubs Hub',
      content: 'Universal paystub center for every person type. Preview the full PDF inline, edit the gross amount (manual override per week), email the paystub, and view YTD totals.',
      buttons: stepButtons },
    { target: '[data-testid="nav-schedule"]', placement: 'right',
      title: '📅 Schedule',
      content: 'Drag-and-drop Gantt scheduler. Move single weeks between drivers — split/merge happens automatically. Color-coded by driver, with conflict detection.',
      buttons: stepButtons },
    { target: '[data-testid="nav-history"]', placement: 'right',
      title: '📜 History',
      content: 'A complete audit trail of past weeks: loads, settlements, paystubs, deductions, who-paid-whom. Search and export by any date range.',
      buttons: stepButtons },
    { target: '[data-testid="nav-vehicle-mgmt"]', placement: 'right',
      title: '🔧 Vehicle Management',
      content: 'Maintenance records, expiring inspection alerts, repair history, mileage tracking, and document expiry reminders. Stay ahead of DOT compliance.',
      buttons: stepButtons },
    { target: '[data-testid="nav-driver-files"]', placement: 'right',
      title: '📁 Driver Files',
      content: 'Upload and organize CDLs, medical cards, drug-test results, employment contracts. One folder per driver, with expiry warnings.',
      buttons: stepButtons },
    { target: '[data-testid="nav-accounting"]', placement: 'right',
      title: '🧮 Accounting',
      content: 'Revenue / expense ledger, profit-and-loss snapshots, AI-assisted fuel report extraction, and exportable financial summaries.',
      buttons: stepButtons },
    { target: '[data-testid="nav-company-settings"]', placement: 'right',
      title: '⚙️ Company Settings',
      content: 'Customize branding (logo, PDF colors), toggle features on/off, configure granular Roles & Permissions, invite team members, and reach support via the Contact tab.',
      buttons: stepButtons },
    {
      target: 'body',
      placement: 'center',
      title: '🚀 You\'re all set',
      content: 'Suggested first run: add a Unit → add a Driver → add a Load → check Summary on Friday. Need help? Hit the AI chat icon any time, or visit Company Settings → Contact.',
      buttons: ['back', 'close', 'primary'],
    },
  ];

  /**
   * v3 onEvent receives (data, controls). data.status reflects the tour state.
   * When the user clicks the X (action='close') or Skip, v3 fires the event but
   * doesn't auto-stop the tour — we have to call controls.stop() / controls.skip()
   * ourselves. We also unmount immediately so the dimmed overlay can never get stuck.
   */
  const handleEvent = (data, controls) => {
    const status = data?.status;
    const action = data?.action;
    const finished = status === STATUS.FINISHED || status === STATUS.SKIPPED;
    const userClosed = action === 'close' || action === 'skip';

    if (userClosed && controls?.stop) {
      try { controls.stop(); } catch { /* noop */ }
    }

    if (finished || userClosed) {
      setRun(false);
      const skipped = status === STATUS.SKIPPED || action === 'close' || action === 'skip';
      onboardingApi.complete(skipped).catch(() => {});
      onClose && onClose();
    }
  };

  if (!run) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      disableOverlayClose
      scrollToFirstStep
      scrollOffset={80}
      onEvent={handleEvent}
      locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip tour' }}
      styles={{
        options: {
          primaryColor: '#2563eb',
          textColor: '#0f172a',
          backgroundColor: '#ffffff',
          arrowColor: '#ffffff',
          zIndex: 10000,
        },
        tooltip: { borderRadius: 12, fontSize: 14 },
        tooltipTitle: { fontSize: 16, fontWeight: 700 },
        buttonNext: { borderRadius: 6, fontWeight: 600 },
        buttonBack: { color: '#475569' },
      }}
    />
  );
}
